import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/projetosAdvantec.css';
import '../styles/dreVertical.css';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};
const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const pct1 = (n) => String(Number(n || 0).toFixed(1)).replace('.', ',');

// Mês que a página já abre selecionado (8 = Agosto), mesmo critério do DRE horizontal
const MES_PADRAO = 8;

// Card de KPI (fora do componente: recriá-lo a cada render remontaria e re-animaria)
const Kpi = ({ i, icone, tone, titulo, valor, corValor, sub }) => (
  <div className="pa-kpi pa-in" style={{ '--i': i }}>
    <div className="pa-kpi-top">
      <span className={`pa-kpi-icon pa-tone-${tone}`}><i className={`fas ${icone}`}></i></span>
      <span className="pa-eyebrow">{titulo}</span>
    </div>
    <div className={`pa-kpi-value pa-num ${corValor || ''}`}>{valor}</div>
    {sub && <div className="pa-kpi-sub">{sub}</div>}
  </div>
);

export default function Dre_vertical() {
  const { cliente_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const anoInicial = parseInt(searchParams.get('ano') || new Date().getFullYear());

  const [ano, setAno] = useState(anoInicial);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [mesIni, setMesIni] = useState(MES_PADRAO);
  const [mesFim, setMesFim] = useState(MES_PADRAO);
  // pilares (departamentos) abertos — começa VAZIO: o DRE por pilar entra todo
  // fechado e o usuário abre o que quer ver
  const [pilaresAbertos, setPilaresAbertos] = useState(new Set());
  const togglePilar = (nome) => setPilaresAbertos(prev => {
    const next = new Set(prev);
    next.has(nome) ? next.delete(nome) : next.add(nome);
    return next;
  });
  // lançamentos inline por categoria: chave `${tipo}_${codigo}`
  const [expandidas, setExpandidas] = useState({});
  const [lancamentos, setLancamentos] = useState({});
  const [carregLanc, setCarregLanc] = useState({});

  const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('access')}` });
  const API = process.env.REACT_APP_API_BASE;

  // Filtro GERAL de status (topo) — ref evita closure desatualizada no buscar
  const filtroLancRef = useRef('pago');
  // Consolidar empresas do grupo (25 + 27): soma os dois DREs em um só
  const [consolidado, setConsolidado] = useState(false);
  const consolidadoRef = useRef(false);

  const buscar = useCallback(async (mi, mf, st) => {
    setCarregando(true);
    try {
      const res = await axios.get(`${API}/api/dre-completo-ytd/`,
        { params: { cliente_id, ano, mes_ini: mi, mes_fim: mf, status: st || filtroLancRef.current,
                    ...(consolidadoRef.current ? { consolidar: 1 } : {}) }, headers: headers() });
      if (res.data.success) setDados(res.data);
      else setDados({ erro: res.data.erro || 'Falha ao gerar DRE.' });
    } catch (e) {
      setDados({ erro: e.response?.data?.erro || 'Erro ao carregar DRE.' });
    } finally {
      setCarregando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente_id, ano]);

  useEffect(() => {
    if (!cliente_id) return;
    setMesIni(MES_PADRAO); setMesFim(MES_PADRAO);
    setExpandidas({}); setLancamentos({}); setPilaresAbertos(new Set());
    buscar(MES_PADRAO, MES_PADRAO);
  }, [cliente_id, ano, buscar]);

  // `departamento` vem da categoria quando o DRE está em pilares (detalhe = fatia).
  // `origens` vem do DRE CONSOLIDADO: a categoria pode existir nas duas empresas,
  // com códigos diferentes — busca os lançamentos de cada uma e junta.
  const toggleLanc = async (tipo, codigo, prefixo = '', departamento, origens) => {
    const key = `${tipo}_${prefixo}${codigo}`;
    if (expandidas[key]) { setExpandidas(p => ({ ...p, [key]: false })); return; }
    setExpandidas(p => ({ ...p, [key]: true }));
    if (lancamentos[key]) return;
    setCarregLanc(p => ({ ...p, [key]: true }));
    try {
      const nomes = {};
      (dados?.clientes || []).forEach(cl => { nomes[String(cl.id)] = cl.nome; });
      const alvos = (origens && origens.length) ? origens : [{ cliente_id, codigo, departamento }];
      const listas = await Promise.all(alvos.map(async (o) => {
        const params = { cliente_id: o.cliente_id ?? cliente_id, ano, mes_ini: mesIni, mes_fim: mesFim,
                         codigo_categoria: o.codigo ?? codigo, status: filtroLancRef.current };
        if (o.departamento) params.departamento = o.departamento;
        const res = await axios.get(`${API}/api/dre-horizontal-lancamentos/`, { params, headers: headers() });
        const itens = res.data.success ? (res.data.lancamentos || []) : [];
        const empresa = alvos.length > 1 ? (nomes[String(o.cliente_id)] || `Cliente ${o.cliente_id}`) : null;
        return empresa ? itens.map(l => ({ ...l, empresa })) : itens;
      }));
      const todos = [].concat(...listas).sort((x, y) => String(x.data).localeCompare(String(y.data)));
      setLancamentos(p => ({ ...p, [key]: todos }));
    } catch (e) {
      console.error('Erro ao buscar lançamentos:', e);
    } finally {
      setCarregLanc(p => ({ ...p, [key]: false }));
    }
  };

  // Filtro GERAL de status (pílulas no topo) — filtra o DRE inteiro no backend.
  // Padrão do vertical: 'pago' (só realizados, comportamento original)
  const [filtroLanc, setFiltroLanc] = useState('pago');
  const FILTROS_LANC = [
    { key: 'todos', rotulo: 'Todos' },
    { key: 'pago', rotulo: 'Pago' },
    { key: 'aberto', rotulo: 'Aberto' },
    { key: 'vencido', rotulo: 'Vencido' },
  ];
  const aplicarFiltroLanc = (k) => {
    setFiltroLanc(k);
    filtroLancRef.current = k;
    setExpandidas({});
    setLancamentos({});
    buscar(mesIni, mesFim, k);
  };

  const clicarMes = (mes) => {
    let ini = mesIni, fim = mesFim;
    if (mes < mesIni) { ini = mes; }
    else if (mes > mesFim) { fim = mes; }
    else if (mes === mesIni && mes !== mesFim) { ini = mes + 1; }
    else if (mes === mesFim && mes !== mesIni) { fim = mes - 1; }
    else { ini = mes; fim = mes; }
    // limpa lançamentos expandidos: eram do período anterior
    // (cache por categoria não considera o mês selecionado)
    setMesIni(ini); setMesFim(fim); setExpandidas({}); setLancamentos({}); buscar(ini, fim);
  };

  const voltarDashboard = async () => {
    // dashboard certo depende do SISTEMA do cliente (Omie × Conta Azul)
    let rota = `/dashboard-cliente/${cliente_id}`;
    try {
      const r = await axios.get(`${API}/api/cliente-bpo/${cliente_id}/`, { headers: headers() });
      if ((r.data?.cliente?.sistema || '').toLowerCase() === 'conta_azul') rota = `/dashboard-conta-azul/${cliente_id}`;
    } catch (e) { /* mantém a rota padrão */ }
    navigate(rota);
  };

  const anoAtual = new Date().getFullYear();
  const anosOpcoes = [anoAtual + 1, anoAtual, anoAtual - 1, anoAtual - 2];

  // Seção (receitas ou despesas) de um bloco: grupos → categorias → lançamentos inline
  const renderSecao = (titulo, bloco, tipo, prefixo = '') => {
    const grupos = bloco?.grupos || [];
    const cls = tipo === 'receitas' ? 'is-rec' : 'is-desp';
    return (
      <>
        <tr className={`dre-sec ${cls}`}>
          <td colSpan={2}>{titulo}</td>
        </tr>
        {grupos.map((g, gi) => (
          <React.Fragment key={`${g.pilar || ''}_${g.codigo_dre}_${gi}`}>
            <tr className="dre-grupo">
              <td>{g.descricao_dre}</td>
              <td className="dre-val">{formatarValor(g.total)}</td>
            </tr>
            {g.categorias.map((cat, i) => {
              const key = `${tipo}_${prefixo}${cat.codigo}`;
              const aberto = expandidas[key];
              return (
                <React.Fragment key={i}>
                  <tr className={`dre-cat ${cls} ${aberto ? 'is-open' : ''}`}
                      onClick={() => toggleLanc(tipo, cat.codigo, prefixo, cat.departamento, cat.origens)}
                      title="Ver lançamentos desta categoria">
                    <td><i className="fas fa-chevron-right chev"></i>{cat.descricao}</td>
                    <td className="dre-val">
                      {tipo === 'receitas' ? formatarValor(cat.total) : `(${formatarValor(cat.total)})`}
                    </td>
                  </tr>
                  {aberto && (
                    <tr>
                      <td colSpan={2} className={`dre-lanc-wrap ${cls}`}>
                        {carregLanc[key] ? (
                          <div className="dre-lanc-load"><div className="pa-spinner"></div></div>
                        ) : (() => {
                          const itensLanc = (lancamentos[key] || []);
                          return (
                            <table className="dre-lanc">
                              <thead><tr>
                                <th>Vencimento</th>
                                <th>Fornecedor / Cliente</th>
                                <th>Status</th>
                                <th className="dre-val">Valor</th>
                              </tr></thead>
                              <tbody>
                                {itensLanc.length === 0 ? (
                                  <tr><td colSpan={4} className="vazio">
                                    {filtroLanc !== 'todos' ? 'Nenhum lançamento com esse status no período.' : 'Nenhum lançamento no período.'}
                                  </td></tr>
                                ) : itensLanc.map((lc, li) => (
                                  <tr key={li}>
                                    <td className="pa-tab-num">{lc.data ? new Date(lc.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                    <td>
                                      {lc.empresa && <span className="pa-chip" style={{ height: 22, fontSize: 11, marginRight: 8 }}>{lc.empresa}</span>}
                                      {lc.nome || '—'}
                                    </td>
                                    <td><span className="pa-chip" style={{ height: 22, fontSize: 11 }}>{lc.status || '—'}</span></td>
                                    <td className={`dre-val ${lc.valor < 0 ? 'pa-text-red' : 'pa-text-green'}`}>{formatarValor(lc.valor)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        })()}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
        <tr className={`dre-sec-total ${cls}`}>
          <td>(=) Total {titulo.replace(/^\(.\)\s*/, '').toLowerCase()}</td>
          <td className="dre-val">{formatarValor(bloco?.total || 0)}</td>
        </tr>
      </>
    );
  };

  const resultado = dados?.resultado || 0;
  const totRec = dados?.receitas?.total || 0;
  const totDesp = dados?.despesas?.total || 0;
  const margem = totRec > 0 ? (resultado / totRec) * 100 : null;
  const temPilares = Array.isArray(dados?.pilares) && dados.pilares.length > 0;
  const nomeEmpresa = dados?.consolidado ? (dados.clientes || []).map(cl => cl.nome).join(' + ') : null;

  return (
    <div className="pa-page dre-page">
      {/* ---------- cabeçalho ---------- */}
      <header className="pa-header pa-in" style={{ '--i': 0 }}>
        <div>
          <div className="pa-eyebrow">DRE · Demonstração do resultado · {ano}</div>
          <h1 className="pa-title">{nomeEmpresa || 'Resultado do período'}</h1>
          <p className="pa-subtitle">
            Competência por <strong>data de vencimento</strong>
            {dados?.periodo_label ? ` · ${dados.periodo_label}` : ''}
            {temPilares ? ' · conciliado por departamento' : ''}
          </p>
        </div>
        <div className="pa-header-right">
          {dados?.consolidado && (
            <span className="pa-chip pa-chip-gold"><i className="fas fa-layer-group"></i>Grupo consolidado</span>
          )}
          <select className="pa-input dre-year" value={ano} onChange={e => setAno(parseInt(e.target.value))} aria-label="Ano">
            {anosOpcoes.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button type="button" className="pa-btn pa-btn-ghost" onClick={voltarDashboard}>
            <i className="fas fa-arrow-left"></i>Dashboard
          </button>
        </div>
      </header>

      {/* ---------- filtros ---------- */}
      <section className="pa-toolbar dre-toolbar pa-in" style={{ '--i': 1 }} aria-label="Filtros">
        <div className="pa-months" role="group" aria-label="Meses">
          {MESES_ABREV.map((m, i) => {
            const mes = i + 1;
            const ativo = mes >= mesIni && mes <= mesFim;
            return (
              <button key={mes} type="button" className={ativo ? 'is-on' : ''} aria-pressed={ativo}
                onClick={() => clicarMes(mes)}>{m}</button>
            );
          })}
        </div>
        <span className="pa-sep"></span>
        <div className="pa-seg" role="tablist" aria-label="Status">
          {FILTROS_LANC.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={filtroLanc === f.key}
              className={filtroLanc === f.key ? 'is-on' : ''} onClick={() => aplicarFiltroLanc(f.key)}>{f.rotulo}</button>
          ))}
        </div>
        {(dados?.consolidavel || consolidado) && (
          <>
            <span className="pa-sep"></span>
            <button type="button" className={`pa-btn pa-btn-toggle ${consolidado ? 'is-on' : ''}`} aria-pressed={consolidado}
              title="Somar este DRE com o da outra empresa do grupo (Advantec + Advwg)"
              onClick={() => {
                const v = !consolidado;
                setConsolidado(v); consolidadoRef.current = v;
                setExpandidas({}); setLancamentos({});
                buscar(mesIni, mesFim);
              }}>
              <i className="fas fa-layer-group"></i>{consolidado ? 'Consolidado' : 'Consolidar empresas'}
            </button>
          </>
        )}
        {carregando && <div className="pa-spinner" aria-label="Carregando"></div>}
      </section>

      {carregando && !dados ? (
        <div className="pa-kpis">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="pa-skel-card">
              <div className="pa-skel pa-skel-line" style={{ width: '45%' }}></div>
              <div className="pa-skel pa-skel-num"></div>
              <div className="pa-skel pa-skel-line" style={{ width: '70%' }}></div>
            </div>
          ))}
        </div>
      ) : !dados ? null : dados.erro ? (
        <div className="dre-alert pa-in" style={{ '--i': 2 }}><i className="fas fa-triangle-exclamation me-2"></i>{dados.erro}</div>
      ) : (
        <>
          {/* ---------- KPIs do período ---------- */}
          <div className="pa-kpis" style={{ marginBottom: 20 }}>
            <Kpi i={2} icone="fa-hand-holding-dollar" tone="green" titulo="Receitas" valor={formatarValor(totRec)} corValor="pa-text-green"
              sub={`${(dados.receitas?.grupos || []).length || (temPilares ? dados.pilares.length : 0)} ${temPilares ? 'departamento(s)' : 'grupo(s)'} no período`} />
            <Kpi i={3} icone="fa-coins" tone="red" titulo="Despesas" valor={formatarValor(totDesp)} corValor="pa-text-red"
              sub={totRec > 0 ? `${pct1((totDesp / totRec) * 100)}% das receitas` : 'Sem receita no período'} />
            <Kpi i={4} icone="fa-scale-balanced" tone={resultado >= 0 ? 'green' : 'red'} titulo="Resultado"
              valor={formatarValor(resultado)} corValor={resultado >= 0 ? 'pa-text-green' : 'pa-text-red'}
              sub="Receitas − despesas" />
            <Kpi i={5} icone="fa-percent" tone={margem === null ? 'navy' : margem >= 0 ? 'green' : 'red'} titulo="Margem"
              valor={margem === null ? '—' : `${pct1(margem)}%`} corValor={margem === null ? '' : margem >= 0 ? 'pa-text-green' : 'pa-text-red'}
              sub="Resultado sobre as receitas" />
          </div>

          {temPilares ? (
            <>
              {/* DRE conciliado por DEPARTAMENTO: um card por pilar, fechado por padrão */}
              <div className="dre-pilares">
                {dados.pilares.map((p, pi) => {
                  const aberto = pilaresAbertos.has(p.departamento);
                  const rec = p.receitas?.total || 0;
                  const desp = p.despesas?.total || 0;
                  const base = rec + desp;
                  return (
                    <div key={`${p.departamento}_${pi}`} className={`pa-card dre-pilar pa-in ${aberto ? 'is-open' : ''}`} style={{ '--i': 6 + pi }}>
                      <div className="dre-pilar-head" role="button" tabIndex={0} aria-expanded={aberto}
                        onClick={() => togglePilar(p.departamento)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePilar(p.departamento); } }}
                        title={aberto ? 'Recolher este departamento' : 'Abrir receitas e despesas deste departamento'}>
                        <span className="dre-pilar-chev"><i className="fas fa-chevron-right"></i></span>
                        <span className="dre-pilar-nome">{p.departamento}</span>
                        <span className="dre-pilar-meta">
                          <span><span className="pa-dot" style={{ background: 'var(--pa-green)' }}></span>Receitas <b>{formatarValor(rec)}</b></span>
                          <span><span className="pa-dot" style={{ background: 'var(--pa-red)' }}></span>Despesas <b>{formatarValor(desp)}</b></span>
                        </span>
                        <span className={`dre-result ${p.resultado >= 0 ? 'is-pos' : 'is-neg'}`}>
                          <i className={`fas fa-arrow-${p.resultado >= 0 ? 'up' : 'down'}`}></i>{formatarValor(p.resultado)}
                        </span>
                      </div>
                      <div className="dre-mix" aria-hidden="true">
                        <span className="is-rec" style={{ width: `${base > 0 ? (rec / base) * 100 : 0}%` }}></span>
                        <span className="is-desp" style={{ width: `${base > 0 ? (desp / base) * 100 : 0}%` }}></span>
                      </div>
                      {aberto && (
                        <div className="dre-pilar-body">
                          <table className="dre-table">
                            <colgroup><col style={{ width: '68%' }} /><col style={{ width: '32%' }} /></colgroup>
                            <tbody>
                              {(p.receitas?.grupos || []).length > 0 && renderSecao('(+) Receitas', p.receitas, 'receitas', `p${pi}_`)}
                              {(p.despesas?.grupos || []).length > 0 && renderSecao('(−) Despesas', p.despesas, 'despesas', `p${pi}_`)}
                              <tr className="dre-pilar-result">
                                <td>(=) Resultado — {p.departamento}</td>
                                <td className={`dre-val ${p.resultado >= 0 ? 'is-pos' : 'is-neg'}`}>{formatarValor(p.resultado)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="dre-total pa-in" style={{ '--i': 6 + dados.pilares.length }}>
                <div className="dre-total-lbl">
                  <span className="pa-eyebrow">Resultado líquido</span>
                  <strong>Todos os departamentos</strong>
                </div>
                <span className={`pa-num ${resultado >= 0 ? 'is-pos' : 'is-neg'}`}>{formatarValor(resultado)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="pa-card pa-in" style={{ '--i': 6, marginBottom: 16 }}>
                <table className="dre-table">
                  <colgroup><col style={{ width: '68%' }} /><col style={{ width: '32%' }} /></colgroup>
                  <tbody>
                    {renderSecao('(+) Receitas', dados.receitas, 'receitas')}
                    {renderSecao('(−) Despesas', dados.despesas, 'despesas')}
                  </tbody>
                </table>
              </div>
              <div className="dre-total pa-in" style={{ '--i': 7 }}>
                <div className="dre-total-lbl">
                  <span className="pa-eyebrow">Resultado líquido</span>
                  <strong>{dados?.periodo_label || 'Período selecionado'}</strong>
                </div>
                <span className={`pa-num ${resultado >= 0 ? 'is-pos' : 'is-neg'}`}>{formatarValor(resultado)}</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
